// Icons — Lucide (2px stroke, currentColor), per the Mockingbird design system.
// Keeps the string-keyed `name` API so module manifests + call sites are
// unchanged. The brand mark is an asset, NEVER a line icon (system rule).

import {
  AlertTriangle,
  FlaskConical,
  AudioWaveform,
  ListTree,
  NotebookPen,
  Table2,
  Upload,
  Headphones,
  ScanText,
  FileText,
  Plus,
  Play,
  Pause,
  SkipBack,
  SkipForward,
  RotateCcw,
  RotateCw,
  Repeat,
  Check,
  Flag,
  ArrowLeft,
  ArrowRightLeft,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  Folder,
  GripVertical,
  LayoutGrid,
  List,
  MoreHorizontal,
  Archive,
  ArchiveRestore,
  Trash2,
  ToggleRight,
  Square,
  Info,
  Menu,
  Share2,
  Download,
  X,
  FileSpreadsheet,
  Search,
  MapPin,
  Copy,
  Keyboard,
  Mic,
  Ban,
  Receipt,
  Settings,
  Bell,
  Pencil,
  ExternalLink,
  LayoutDashboard,
  ZoomIn,
  ZoomOut,
  PenLine,
  MessageSquare,
  type LucideIcon,
} from "lucide-react";
import markGradient from "../brand/assets/mark-gradient.png";

const MAP: Record<string, LucideIcon> = {
  // Left-drawer nav glyphs (Mockingbird design system — distinct metaphors:
  // listening / the signal / scanning text, so no two read as the same shape).
  headphones: Headphones,       // Proofing
  "audio-waveform": AudioWaveform, // Audio analysis
  "scan-text": ScanText,        // Manuscript analysis
  proofing: Headphones,         // legacy alias → new glyph
  waveform: AudioWaveform,      // legacy alias
  document: FileText,
  plus: Plus,
  play: Play,
  pause: Pause,
  "skip-back": SkipBack,
  "skip-forward": SkipForward,
  "rotate-ccw": RotateCcw,
  "rotate-cw": RotateCw,
  repeat: Repeat,
  check: Check,
  flag: Flag,
  back: ArrowLeft,
  transfer: ArrowRightLeft,     // move/copy audio to another project
  "chevron-left": ChevronLeft,
  "chevron-right": ChevronRight,
  "chevron-up": ChevronUp,
  "chevron-down": ChevronDown,
  folder: Folder,
  grip: GripVertical,
  grid: LayoutGrid,
  list: List,
  more: MoreHorizontal,
  menu: Menu,
  info: Info,
  share: Share2,
  download: Download,
  x: X,
  "file-spreadsheet": FileSpreadsheet,
  "file-text": FileText,
  search: Search,
  "zoom-in": ZoomIn,
  "zoom-out": ZoomOut,
  "map-pin": MapPin,
  copy: Copy,
  keyboard: Keyboard,
  mic: Mic,
  ban: Ban,
  archive: Archive,
  restore: ArchiveRestore,
  trash: Trash2,
  toggle: ToggleRight, // Admin → Feature switches
  receipt: Receipt,
  settings: Settings,
  bell: Bell,
  edit: Pencil,
  external: ExternalLink,
  workflows: LayoutDashboard,   // top-level Workflows production board
  "list-tree": ListTree,        // Table of Contents module
  table: Table2,
  notebook: NotebookPen,        // add a reviewer note to a flagged cell
  upload: Upload,
  alert: AlertTriangle,
  flask: FlaskConical,          // Noise Lab (superadmin training-data space)
  "pen-line": PenLine,          // a mark the script makes (flag cards)
  message: MessageSquare,       // a Word comment on the script
};

export function Icon({
  name,
  size = 18,
  className,
}: {
  name: string;
  size?: number;
  className?: string;
}) {
  if (name === "logo") {
    return (
      <img
        src={markGradient}
        width={size}
        height={size}
        className={className}
        alt=""
        aria-hidden="true"
        style={{ objectFit: "contain", display: "block" }}
      />
    );
  }
  const Cmp = MAP[name] ?? Square;
  return <Cmp size={size} strokeWidth={2} className={className} aria-hidden="true" />;
}
